/**
 * taxAnnotate.js — the DERIVED-annotation pass over a tax formula workspace (S1-P10).
 *
 * Everything here is DISPLAY-ONLY. None of it is compiled and none of it is persisted (see each
 * saveExtraState in taxBlocks.js), so re-deriving it can never drift the expression — and all of it
 * MUST be re-derived on every load/change, or a block contradicts itself: a stored graph carries the
 * name it was authored with, and nothing rewrites it when the rule/ค่าคงที่/สูตร behind it is deleted.
 *
 * It lives OUTSIDE the view for one reason: it is the only place that knows a `tax_path` is annotated
 * exactly like a `tax_variable`. Inside TaxFormulaBlockly.vue the pass filtered on
 * `b.type !== 'tax_variable'`, so a block loaded from a stored graph kept the CACHED `flagged` bit
 * from its extraState — stale whenever the rule was deleted after the last save — and never got a
 * gloss at all. A headless module is a testable one, which is what stops that from being true again.
 *
 * PURE-ish: imports only the shared Blockly core (headless-safe) + the two resolvers the flyout uses,
 * so the toolbox seeds and this pass can never disagree about what a name IS.
 */
import Blockly from './blocklyCore.js'
import { classifyField, pathRoot, pathDisplay } from '../formulaGraph.js'
import { describeVariable } from './taxToolbox.js'

const VARIABLE_TYPE = 'tax_variable'
const PATH_TYPE = 'tax_path'

/** Fallback tooltip for a tax_variable whose NAME resolves to nothing (setTooltip replaces the def's). */
export const VARIABLE_TOOLTIP = 'ตัวแปร: อ้างชื่อ rule / ข้อมูลส่วนตัว / global / ค่าคงที่ / ผลลัพธ์สูตรอื่น'

/** Accept a catalog entry as a plain string or a {name, description} DTO row. */
function normalizeVars(arr) {
  return (Array.isArray(arr) ? arr : [])
    .map((v) => {
      if (!v) return null
      if (typeof v === 'string') return { name: v.trim(), description: '' }
      const name = String(v.name != null ? v.name : (v.Name != null ? v.Name : '')).trim()
      const description = String(v.description != null ? v.description : (v.Description != null ? v.Description : ''))
      return name ? { name, description } : null
    })
    .filter(Boolean)
}

/** Accept a path entry as a plain string or a {path|name, description|label} row. */
function normalizePaths(arr) {
  return (Array.isArray(arr) ? arr : [])
    .map((v) => {
      if (!v) return null
      if (typeof v === 'string') return { path: v.trim(), description: '' }
      const path = String(v.path != null ? v.path : (v.Path != null ? v.Path : (v.name != null ? v.name : (v.Name != null ? v.Name : '')))).trim()
      const description = String(v.description != null ? v.description : (v.Description != null ? v.Description : (v.label != null ? v.label : '')))
      return path ? { path, description } : null
    })
    .filter(Boolean)
}

/**
 * Shape `GET /TaxCalculate/variables` into the group lists the toolbox ctx and classifyField want.
 *
 * THREE groups come back, always — `paths`, `personalInfo`, `globals` — whatever the payload had.
 * An absent group is the NORMAL state of a backend that has not shipped it yet, and it must degrade to
 * "this category is empty" rather than to a broken editor: the whole catalog is best-effort
 * (`getVariables().catch(() => null)`), and a formula is correct or not regardless of whether a fetch
 * answered. This function only supplies NAMES and GLOSSES; nothing here decides what is legal.
 *
 * `paths` is the one group the tax_path block cannot be authored without — its PATH label is fixed
 * text, so the flyout is the only way to get one onto the canvas. Items arrive shaped like every other
 * TaxVariableDto (`{name, source, description, dataType}`), with `name` carrying the FULL path text
 * (`INCOME_40_5.subItems[].items[].amount`); `{path, label}` rows are accepted too, so a caller that
 * already speaks the toolbox's shape does not have to be rewritten.
 *
 * (There is deliberately no `itemScope` group any more: item_amount/item_rate/item_expense were bound
 * only inside a SUMITEMS body, and both are retired — see formulaGraph's RETIRED_IDENTIFIERS. A
 * backend that still ships them is ignored here rather than offered, because offering a retired name
 * is offering a save-time 400.)
 *
 * @param {?Object} variables TaxVariableCatalogDto ({personalInfo, globals, paths?}) — null when the fetch failed
 * @returns {{paths:Array, personalInfo:Array, globals:Array}} always all three groups
 */
export function buildVariableCatalog(variables) {
  const v = variables || {}
  return {
    paths: normalizePaths(v.paths != null ? v.paths : v.Paths),
    personalInfo: normalizeVars(v.personalInfo != null ? v.personalInfo : v.PersonalInfo),
    globals: normalizeVars(v.globals != null ? v.globals : v.Globals),
  }
}

/**
 * Re-derive the badge + gloss + stale "!" + tooltip of every annotated block on the canvas from the
 * LIVE catalog. Covers BOTH block types that name something an admin can delete from the version:
 *   • tax_variable   — its NAME (a rule bridge var / personal / global / ค่าคงที่ / สูตร)
 *   • tax_path       — its ROOT (a rule code, resolved through the very same ruleInputs group)
 *
 * @param {Object} workspace a Blockly workspace (headless or rendered)
 * @param {Object} options
 * @param {Object|Function} options.ctx      toolbox ctx, or a getter for it (tooltips resolve lazily,
 *                                           so a hovered block reads current between passes)
 * @param {Object} options.catalog           classifyField catalog (see TaxFormulaBlockly.classifyCatalog)
 * @param {boolean} options.canFlag          may a "not in the catalog" be reported as deleted? Only when
 *                                           EVERY group is real — a failed fetch would otherwise flag the
 *                                           whole canvas at once, and an alarm on every block is an alarm
 *                                           on none. Defaults to false: silence beats a false accusation.
 * @param {string} [options.variableTooltip] fallback tooltip for an unresolved tax_variable
 */
export function applyBlockAnnotations(workspace, options) {
  if (!workspace || typeof workspace.getAllBlocks !== 'function') return
  const o = options || {}
  const getCtx = typeof o.ctx === 'function' ? o.ctx : () => o.ctx || {}
  const catalog = o.catalog || {}
  const canFlag = o.canFlag === true
  const fallbackTooltip = o.variableTooltip || VARIABLE_TOOLTIP
  const ctx = getCtx()
  // Events off: annotating must neither dirty the workspace nor re-enter the view's change listener.
  const disable = Blockly.Events && typeof Blockly.Events.disable === 'function'
  if (disable) Blockly.Events.disable()
  try {
    workspace.getAllBlocks(false).forEach((b) => {
      if (!b || typeof b.setTaxFlagged !== 'function' || typeof b.setTaxDescription !== 'function') return
      if (b.type === VARIABLE_TYPE) {
        const name = b.getFieldValue('NAME')
        const cls = classifyField(name, catalog)
        // The badge IS persisted, so this rewrites a stored source that no longer matches the catalog —
        // correcting a wrong badge, not inventing one: classifyField is what the flyout seeds from.
        b.setTaxSource(cls.source)
        b.setTaxFlagged(canFlag && cls.flagged)
        b.setTaxDescription(describeVariable(name, ctx).desc)
        try {
          b.setTooltip(() => describeVariable(b.getFieldValue('NAME'), getCtx()).tooltip || fallbackTooltip)
        } catch (_e) { /* not rendered — ignore */ }
      } else if (b.type === PATH_TYPE) {
        // Annotated exactly like a tax_variable, and for the same reason it lives here rather than in
        // the view: a stored graph carries a CACHED `flagged`/badge, and the keep-the-layout load path
        // would otherwise show a "!" (or the absence of one) from whenever it was saved.
        //
        // The gloss and the staleness are resolved from the ROOT only. The catalog knows which tax
        // rules exist; it has no picture of the JSON hanging off one, so `subItems`/`amount` cannot be
        // checked here — a wrong SEGMENT is provable only at run time, from the data. Deriving a gloss
        // for the root and showing the readable `▸` spelling is everything this pass honestly can do.
        const path = typeof b.getTaxPath === 'function' ? b.getTaxPath() : ''
        const root = pathRoot(path)
        const cls = classifyField(path, catalog)
        b.setTaxFlagged(canFlag && cls.flagged)
        const rootDesc = root ? describeVariable(root, ctx).desc : ''
        b.setTaxDescription(rootDesc)
        try {
          b.setTooltip(() => {
            const p = typeof b.getTaxPath === 'function' ? b.getTaxPath() : ''
            const r = pathRoot(p)
            const base = r ? (describeVariable(r, getCtx()).tooltip || '') : ''
            return (pathDisplay(p) + (base ? ' — ' + base : ''))
              + ' · [] = ทุกรายการในชั้นนั้น (เส้นทางที่ขึ้นต้นเหมือนกันวนรายการเดียวกันพร้อมกัน) และผลของทุกรอบถูกบวกรวมกัน'
          })
        } catch (_e) { /* not rendered — ignore */ }
      }
    })
  } finally {
    if (disable) Blockly.Events.enable()
  }
}

export default { applyBlockAnnotations, buildVariableCatalog, VARIABLE_TOOLTIP }
